/**
 * RealPage Online Leasing (OLL) Block
 *
 * Embeds the RealPage OneSite online leasing widget. The vendor script
 * (widgetLoader.js) reads siteId / ContainerId / bpmid from its own
 * <script src> query string at load time and injects a full leasing
 * application iframe into the matching container div.
 *
 * Note: in sandboxed previews the widget needs `allow-top-navigation`
 * — RealPage sets `#k=<step>` on the top frame for deep-linking during
 * init and will throw SecurityError otherwise, leaving the iframe
 * empty.
 */

import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken, EditableBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';
import { useScriptedContainer } from '../../hooks/useScriptedContainer';

const WIDGET_LOADER_URL = 'https://property.onesite.realpage.com/ollr/widgetLoader.js';
const DEFAULT_BPMID = 'Leasing.GetApproved.Startup.GetWorkflow';
const DEFAULT_MIN_HEIGHT = 877;

function buildScriptSrc(siteId: string, containerId: string, bpmid: string) {
  const params = new URLSearchParams({ siteId, ContainerId: containerId, bpmid });
  return `${WIDGET_LOADER_URL}?${params.toString()}`;
}

function normalize(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function RealpageOllComponent({ sectionId }: BlockProps) {
  const siteId = normalize(
    useBlockToken('site_id', { defaultField: 'custom_realpage_id' }),
  );
  const bpmid =
    normalize(useBlockToken('bpmid', { defaultValue: DEFAULT_BPMID })) ?? DEFAULT_BPMID;
  const minHeightRaw = useBlockToken('min_height', { defaultValue: String(DEFAULT_MIN_HEIGHT) });
  const minHeight = Number(minHeightRaw) || DEFAULT_MIN_HEIGHT;

  const containerId = `OllrDiv-${sectionId}`;
  const wrapperRef = useScriptedContainer({
    containerId,
    scriptSrc: siteId ? buildScriptSrc(siteId, containerId, bpmid) : null,
  });

  if (!siteId) {
    return (
      <div
        className="flex items-center justify-center py-16 text-center bg-muted"
        style={{ color: 'var(--vibe-color-text-muted)' }}
      >
        <div>
          <EditableBlockToken
            localName="site_id"
            type="text"
            label="RealPage Site ID"
            defaultField="custom_realpage_id"
            as="p"
            className="text-lg font-medium"
            style={{ fontFamily: 'var(--vibe-font-heading)' }}
          >
            RealPage Online Leasing
          </EditableBlockToken>
          <p className="text-sm mt-1">
            Configure a RealPage site ID to load the online leasing widget.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      style={{ width: '100%', minHeight: `${minHeight}px` }}
    />
  );
}

const contentSchema: ComponentContentSchema = {
  slot: 'RealpageOll',
  displayName: 'RealPage Online Leasing',
  tokens: {
    site_id: {
      label: 'RealPage Site ID',
      type: 'text',
      defaultField: 'custom_realpage_id',
      suggestedFields: ['custom_realpage_id'],
      description: 'RealPage OneSite property site ID used by the online leasing widget.',
      required: true,
    },
    bpmid: {
      label: 'Business Process (bpmid)',
      type: 'text',
      defaultValue: DEFAULT_BPMID,
      description:
        'RealPage workflow identifier. Default starts the standard "Get Approved" leasing flow.',
    },
    min_height: {
      label: 'Minimum Height (px)',
      type: 'text',
      defaultValue: String(DEFAULT_MIN_HEIGHT),
      description: 'Minimum container height in pixels while the widget loads.',
    },
  },
};

export const realpageOllBlock: BlockDefinition = {
  id: 'realpage-oll',
  name: 'RealPage Online Leasing',
  description:
    'Embed the RealPage OneSite online leasing (OLL) widget for property applications.',
  category: 'Integrations',
  component: RealpageOllComponent,
  contentSchema,
  tags: [
    'realpage',
    'onesite',
    'oll',
    'online-leasing',
    'leasing',
    'application',
    'iframe',
  ],
  defaultGridSpan: 12,
};
